import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { Input as AntInput, Switch } from "antd";
import { Box, CircularProgress, Dialog, Divider, Grid, Typography } from "@mui/material";
import { Plus, X, ChevronDown, ChevronUp } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { callNonStoreApi, callNonStoreApiNew } from "../../NonStoreApis";
import {
  RegisterCommentToDm,
  PauseOrResumeAutomationApi,
  DeleteAutomation,
} from "../../Api/ApiEndPoints";
import {
  generateUUID,
  isStringNullOrUndefined,
  isValidURL,
  logMetaPixelEvent,
  logAnalytics,
  handleImageUpload,
} from "../../utils";
import useWindowSize from "../../Hooks/useWindowSize";
import { primaryActionColor, primaryActionColorLight } from "../../constants";
import InstagramDMPreview from "./InstagramDMPreview";
import AutomationFailuresPanel from "./AutomationFailuresPanel";
import InstaAutomationFollowUps from "./InstaAutomationFollowUps";
import { DEFAULT_FOLLOW_UPS, validateFollowUps } from "./followUpsLogic";

import {
  PageHeader,
  PageContainer,
  Card,
  Button,
  Badge,
} from "../../components/ui";

const { TextArea } = AntInput;

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

const AUTOMATION_TYPES = {
  REPLY_TO_COMMENTS: "reply-to-comments",
  REQUEST_MOBILE_NUMBER: "request-mobile-number",
  REQUEST_EMAIL: "request-email",
  REQUEST_TO_FOLLOW: "request-to-follow",
};

const DM_MESSAGE_TYPES = {
  SIMPLE: "simple",
  ADVANCED: "advanced",
};

const getValidationForStep = (automationType) => {
  switch (automationType) {
    case AUTOMATION_TYPES.REQUEST_MOBILE_NUMBER:
      return "^\\+\\d{10,15}$";
    case AUTOMATION_TYPES.REQUEST_EMAIL:
      return "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$";
    default:
      return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════
// Minimal styled helpers (scoped to this screen)
// ═══════════════════════════════════════════════════════════════════════

const FullPageOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: #f8fafc;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const SectionLabel = ({ title, description }) => (
  <Box sx={{ position: { md: "sticky" }, top: { md: 80 } }}>
    <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: "4px" }}>
      {title}
    </Typography>
    {description && (
      <Typography sx={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
        {description}
      </Typography>
    )}
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{ fontSize: 13, fontWeight: 500, color: "#334155", mb: "6px" }}
  >
    {children}
  </Typography>
);

const ErrorText = ({ children }) =>
  children ? (
    <Typography sx={{ fontSize: 12, color: "#dc2626", mt: "4px" }}>
      {children}
    </Typography>
  ) : null;

const TriggerOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1.5px solid ${(p) => (p.active ? primaryActionColor : "#e2e8f0")};
  background: ${(p) => (p.active ? primaryActionColorLight : "#fff")};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => (p.active ? primaryActionColor : "#334155")};
  transition: all 0.15s ease;
  flex: 1;
  min-width: 140px;
  &:hover {
    border-color: ${primaryActionColor};
  }
`;

const ReplyModeTab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  color: ${(p) => (p.active ? "#fff" : "#64748b")};
  background: ${(p) => (p.active ? primaryActionColor : "transparent")};
  transition: all 0.15s ease;
`;

const KeywordChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${primaryActionColorLight};
  color: ${primaryActionColor};
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
`;

const ToggleRow = ({ title, subtitle, checked, onChange, last }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: "12px",
      borderBottom: last ? "none" : "1px solid #f1f5f9",
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: "2px" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={onChange}
      style={{ backgroundColor: checked ? primaryActionColor : undefined }}
    />
  </Box>
);

// ═══════════════════════════════════════════════════════════════════════
// Link helper (used in edit mode to decode tracker URLs)
// ═══════════════════════════════════════════════════════════════════════

const extractLinkFromButton = (button) => {
  if (!button?.url) return "";
  try {
    const url = new URL(button.url);
    const link = url.searchParams.get("link");
    if (link) return link;
    if (!button.url.startsWith("https://api.convertdm.com")) {
      return button.url;
    }
  } catch (e) {
    // ignore
  }
  return button.url || "";
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function WebAutomationCreation({
  media,
  automation,
  isEdit,
  onClose,
  onSave,
  instaUserId,
  instaId,
  credentials,
}) {
  const { isMobileView } = useWindowSize();

  // ─── Form state ──────────────────────────────────────────────────

  const [automationName, setAutomationName] = useState("");
  const [triggerType, setTriggerType] = useState("any");
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");

  const [commentRepliesEnabled, setCommentRepliesEnabled] = useState(false);
  const [useAiReplies, setUseAiReplies] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [commentReplies, setCommentReplies] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ]);

  const [requireFollow, setRequireFollow] = useState(false);
  const [followPromptMessage, setFollowPromptMessage] = useState(
    "Please follow me first to receive this content!"
  );
  const [collectEmail, setCollectEmail] = useState(false);
  const [emailPromptMessage, setEmailPromptMessage] = useState(
    "What's your email address?"
  );
  const [collectPhone, setCollectPhone] = useState(false);
  const [phonePromptMessage, setPhonePromptMessage] = useState(
    "What's your phone number? (e.g., +1234567890)"
  );

  const [dmMessageType, setDmMessageType] = useState(DM_MESSAGE_TYPES.SIMPLE);
  const [dmMessage, setDmMessage] = useState("");
  // Simple-mode buttons — up to 2 allowed. Each: { link, text }.
  const [dmButtons, setDmButtons] = useState([]);
  const MAX_BUTTONS = 2;
  const [dmImageUrl, setDmImageUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [advancedResponses, setAdvancedResponses] = useState({});

  const [followUps, setFollowUps] = useState(DEFAULT_FOLLOW_UPS());

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showInsufficientCreditsDialog, setShowInsufficientCreditsDialog] =
    useState(false);

  const [staticMediaId] = useState(
    isEdit ? automation?.mediaData?.mediaId : media?.id || media?.mediaId
  );

  // Initial snapshot for dirty tracking
  const initialSnapshotRef = useRef(null);

  // ─── Load existing automation data (edit mode) ───────────────────

  useEffect(() => {
    if (isEdit && automation) {
      loadAutomationData(automation);
    } else if (media) {
      if (media.isNextPost) {
        setAutomationName("Next Post Automation");
      } else {
        const caption = media.caption || "";
        setAutomationName(
          `Automation for ${caption.substring(0, 30) || "post"}...`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, automation, media]);

  const loadAutomationData = (auto) => {
    try {
      const data = auto.data || {};
      const flow = data.flow || {};

      setAutomationName(auto.name || "");
      setTriggerType(data.keywords?.type || "any");
      setKeywords(data.keywords?.keywords || []);

      if (data.commentReplies) {
        setCommentRepliesEnabled(true);
        if (data.commentReplies.isAi) {
          setUseAiReplies(true);
          setAiContext(data.commentReplies.context || "");
        } else if (Array.isArray(data.commentReplies)) {
          setUseAiReplies(false);
          const replies = [...data.commentReplies];
          while (replies.length < 7) replies.push("");
          setCommentReplies(replies.slice(0, 7));
        }
      }

      if (flow[AUTOMATION_TYPES.REQUEST_TO_FOLLOW]) {
        setRequireFollow(true);
        if (flow.followPrompt?.message?.text) {
          setFollowPromptMessage(flow.followPrompt.message.text);
        }
      }
      if (flow[AUTOMATION_TYPES.REQUEST_EMAIL]) {
        setCollectEmail(true);
        if (flow[AUTOMATION_TYPES.REQUEST_EMAIL].message?.text) {
          setEmailPromptMessage(
            flow[AUTOMATION_TYPES.REQUEST_EMAIL].message.text
          );
        }
      }
      if (flow[AUTOMATION_TYPES.REQUEST_MOBILE_NUMBER]) {
        setCollectPhone(true);
        if (flow[AUTOMATION_TYPES.REQUEST_MOBILE_NUMBER].message?.text) {
          setPhonePromptMessage(
            flow[AUTOMATION_TYPES.REQUEST_MOBILE_NUMBER].message.text
          );
        }
      }

      loadDmMessage(flow.end, data.keywords?.keywords || []);

      // Hydrate follow-up config if the automation doc has it. Merge into
      // DEFAULT_FOLLOW_UPS so missing fields fall back to defaults.
      if (auto.followUps) {
        setFollowUps({
          ...DEFAULT_FOLLOW_UPS(),
          ...auto.followUps,
        });
      }
    } catch (error) {
      console.error("Error loading automation data:", error);
      enqueueSnackbar("Error loading automation data", { variant: "error" });
    }
  };

  const loadDmMessage = (endFlow) => {
    if (!endFlow) return;
    if (endFlow.isAdvanced) {
      setDmMessageType(DM_MESSAGE_TYPES.ADVANCED);
      const responses = {};
      Object.entries(endFlow.data || {}).forEach(([key, value]) => {
        responses[key.toLowerCase()] = {
          message: value.message?.text || "",
          link: extractLinkFromButton(value.message?.buttons?.[0]),
          buttonText: value.message?.buttons?.[0]?.title || "",
          imageUrl: value.message?.imageUrl || "",
        };
      });
      setAdvancedResponses(responses);
      return;
    }
    setDmMessageType(DM_MESSAGE_TYPES.SIMPLE);
    if (endFlow.message) {
      setDmMessage(endFlow.message.text || "");
      if (endFlow.message.imageUrl) setDmImageUrl(endFlow.message.imageUrl);
      if (Array.isArray(endFlow.message.buttons) && endFlow.message.buttons.length > 0) {
        const loaded = endFlow.message.buttons
          .slice(0, MAX_BUTTONS)
          .map((b) => ({
            link: extractLinkFromButton(b) || "",
            text: b.title || "Open Link",
          }))
          .filter((b) => b.link);
        if (loaded.length) setDmButtons(loaded);
      }
    }
  };

  // Snapshot current state into initialSnapshotRef once after data loaded
  useEffect(() => {
    // Use a microtask delay so state-setters in loadAutomationData have been flushed
    const t = setTimeout(() => {
      initialSnapshotRef.current = getSnapshot();
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, automation]);

  const getSnapshot = () => ({
    automationName,
    triggerType,
    keywords: [...keywords],
    commentRepliesEnabled,
    useAiReplies,
    aiContext,
    commentReplies: [...commentReplies],
    requireFollow,
    followPromptMessage,
    collectEmail,
    emailPromptMessage,
    collectPhone,
    phonePromptMessage,
    dmMessageType,
    dmMessage,
    dmButtons: JSON.parse(JSON.stringify(dmButtons)),
    dmImageUrl,
    advancedResponses: JSON.parse(JSON.stringify(advancedResponses)),
    followUps: JSON.parse(JSON.stringify(followUps)),
  });

  const currentSnapshot = getSnapshot();
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return (
      JSON.stringify(currentSnapshot) !==
      JSON.stringify(initialSnapshotRef.current)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSnapshot]);

  // ─── Derived: does the main DM have a CTA? ──────────────────────
  // Follow-ups only make sense when the user has something to click on the
  // main DM. Simple mode => at least one dmButton with label+URL; advanced
  // mode => at least one keyword response with full CTA. Gated in the UI
  // and skipped in validation when false.
  const hasCTA = useMemo(() => {
    if (dmMessageType === DM_MESSAGE_TYPES.ADVANCED) {
      return Object.values(advancedResponses || {}).some(
        (r) =>
          !isStringNullOrUndefined(r?.buttonText) &&
          !isStringNullOrUndefined(r?.link) &&
          isValidURL(r.link)
      );
    }
    return (dmButtons || []).some(
      (b) =>
        !isStringNullOrUndefined(b?.text) &&
        !isStringNullOrUndefined(b?.link) &&
        isValidURL(b.link)
    );
  }, [dmMessageType, dmButtons, advancedResponses]);

  // ─── Validation ──────────────────────────────────────────────────

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (triggerType === "specific" && keywords.length === 0) {
      newErrors.keywords = "Add at least one keyword";
    }

    if (commentRepliesEnabled) {
      if (useAiReplies) {
        if (isStringNullOrUndefined(aiContext)) {
          newErrors.aiContext = "Please provide context for AI replies";
        }
      } else {
        const validReplies = commentReplies.filter((r) => r.trim());
        const uniqueReplies = [...new Set(validReplies)];
        if (uniqueReplies.length < 7) {
          newErrors.commentReplies =
            "Add 7 unique replies so they feel natural";
        }
      }
    }

    if (dmMessageType === DM_MESSAGE_TYPES.SIMPLE) {
      if (isStringNullOrUndefined(dmMessage)) {
        newErrors.dmMessage = "DM message is required";
      }
      const buttonErrors = {};
      dmButtons.forEach((btn, idx) => {
        if (isStringNullOrUndefined(btn.link) && isStringNullOrUndefined(btn.text)) {
          // empty row — ignore (user will either fill or remove)
          return;
        }
        if (isStringNullOrUndefined(btn.link)) {
          buttonErrors[idx] = "URL required";
        } else if (!isValidURL(btn.link)) {
          buttonErrors[idx] = "Enter a valid URL";
        } else if (isStringNullOrUndefined(btn.text)) {
          buttonErrors[idx] = "Button text required";
        }
      });
      if (Object.keys(buttonErrors).length) newErrors.dmButtons = buttonErrors;
    }

    // Skip follow-up validation when there's no CTA in the main DM — the
    // feature is gated off in the UI, so stale config shouldn't block save.
    if (hasCTA) {
      const followUpErrors = validateFollowUps(followUps);
      Object.assign(newErrors, followUpErrors);
    }

    if (dmMessageType === DM_MESSAGE_TYPES.ADVANCED) {
      if (triggerType !== "specific") {
        newErrors.advancedResponses =
          "Advanced mode requires specific keywords";
      } else {
        keywords.forEach((keyword) => {
          const response = advancedResponses[keyword.toLowerCase()];
          if (!response || isStringNullOrUndefined(response.message)) {
            newErrors.advancedResponses = `Add a response for "${keyword}"`;
          }
          if (response && !isStringNullOrUndefined(response.link)) {
            if (!isValidURL(response.link)) {
              newErrors.advancedResponses = `Invalid URL for "${keyword}"`;
            }
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    triggerType,
    keywords,
    commentRepliesEnabled,
    useAiReplies,
    aiContext,
    commentReplies,
    dmMessageType,
    dmMessage,
    dmButtons,
    advancedResponses,
    followUps,
    hasCTA,
  ]);

  // ─── Payload building ────────────────────────────────────────────

  const buildEndFlow = useCallback(() => {
    if (dmMessageType === DM_MESSAGE_TYPES.ADVANCED) {
      const endFlow = { isAdvanced: true, data: {} };
      Object.entries(advancedResponses).forEach(([keyword, response]) => {
        if (!isStringNullOrUndefined(response.link)) {
          endFlow.data[keyword] = {
            message: {
              type: "buttons",
              text: response.message,
              ...(response.imageUrl && { imageUrl: response.imageUrl }),
              buttons: [
                {
                  type: "web_url",
                  title: response.buttonText || "Open Link",
                  url: response.link,
                },
              ],
            },
          };
        } else {
          endFlow.data[keyword] = {
            message: {
              type: "text",
              text: response.message,
              ...(response.imageUrl && { imageUrl: response.imageUrl }),
            },
          };
        }
      });
      return endFlow;
    }

    const validButtons = dmButtons
      .filter((b) => !isStringNullOrUndefined(b.link))
      .slice(0, MAX_BUTTONS)
      .map((b) => ({
        type: "web_url",
        title: b.text || "Open Link",
        url: b.link,
      }));

    if (validButtons.length > 0) {
      return {
        message: {
          type: "buttons",
          text: dmMessage,
          ...(dmImageUrl && { imageUrl: dmImageUrl }),
          buttons: validButtons,
        },
      };
    }

    return {
      message: {
        type: "text",
        text: dmMessage,
        ...(dmImageUrl && { imageUrl: dmImageUrl }),
      },
    };
  }, [
    dmMessageType,
    advancedResponses,
    dmButtons,
    dmMessage,
    dmImageUrl,
  ]);

  const buildFlowObject = useCallback(() => {
    const flow = {};
    const mediaId =
      media?.id || media?.mediaId || automation?.mediaData?.mediaId;
    const automationId = isEdit ? automation.id : `auto_${generateUUID()}`;

    const steps = [];
    if (requireFollow) steps.push(AUTOMATION_TYPES.REQUEST_TO_FOLLOW);
    if (collectEmail) steps.push(AUTOMATION_TYPES.REQUEST_EMAIL);
    if (collectPhone) steps.push(AUTOMATION_TYPES.REQUEST_MOBILE_NUMBER);

    const getNextStep = (idx) => steps[idx + 1] || "end";

    steps.forEach((step, index) => {
      const nextStep = getNextStep(index);
      if (step === AUTOMATION_TYPES.REQUEST_TO_FOLLOW) {
        flow[AUTOMATION_TYPES.REQUEST_TO_FOLLOW] = {
          condition: "followerCheck",
          trueStep: nextStep,
          falseStep: "followPrompt",
        };
        flow.followPrompt = {
          message: {
            type: "buttons",
            text: followPromptMessage,
            buttons: [
              {
                type: "web_url",
                title: "Go to my profile",
                url: `https://instagram.com/${
                  credentials?.username || instaUserId
                }`,
              },
              {
                type: "postback",
                title: "I am following you",
                payload: `${mediaId}_${step}_${generateUUID()}_${automationId}`,
                action: AUTOMATION_TYPES.REQUEST_TO_FOLLOW,
              },
            ],
          },
        };
      } else {
        flow[step] = {
          message: {
            type: "text",
            text:
              step === AUTOMATION_TYPES.REQUEST_EMAIL
                ? emailPromptMessage
                : phonePromptMessage,
            validation: getValidationForStep(step),
            nextStep,
          },
        };
      }
    });

    flow.end = buildEndFlow();
    return { flow, automationId };
  }, [
    media,
    automation,
    isEdit,
    requireFollow,
    collectEmail,
    collectPhone,
    followPromptMessage,
    emailPromptMessage,
    phonePromptMessage,
    credentials,
    instaUserId,
    buildEndFlow,
  ]);

  const buildPayload = useCallback(() => {
    const mediaId =
      media?.id || media?.mediaId || automation?.mediaData?.mediaId;
    const { flow, automationId } = buildFlowObject();

    let commentRepliesData = null;
    if (commentRepliesEnabled) {
      if (useAiReplies) {
        commentRepliesData = { isAi: true, context: aiContext };
      } else {
        commentRepliesData = commentReplies.filter((r) => r.trim());
      }
    }

    const mediaType =
      media?.mediaType ||
      media?.media_type ||
      automation?.mediaData?.media_type ||
      "IMAGE";

    const isNextPost = media?.isNextPost || automation?.mediaData?.isNextPost || false;

    const mediaData = isNextPost
      ? {
          isNextPost: true,
          isMediaUpdated: !isEdit,
        }
      : {
          type: "specific",
          mediaId: mediaId,
          media_type: mediaType,
          mediaUrl:
            media?.mediaUrl ||
            media?.thumbnailUrl ||
            automation?.mediaData?.mediaUrl,
          isStory: media?.isStory || automation?.mediaData?.isStory || false,
          isMediaUpdated: staticMediaId !== mediaId,
        };

    const data = {
      skipOpeningDm: true,
      keywords: {
        type: triggerType,
        keywords: triggerType === "specific" ? keywords : [],
      },
      flow,
    };

    if (commentRepliesData) {
      data.commentReplies = commentRepliesData;
    }

    // Auto-derive a name from post caption if the user left it blank.
    const captionForName = (media?.caption || automation?.mediaData?.caption || "").trim();
    const derivedName =
      automationName?.trim() ||
      (captionForName
        ? `Automation for "${captionForName.slice(0, 40)}${
            captionForName.length > 40 ? "…" : ""
          }"`
        : `Automation ${new Date().toLocaleDateString()}`);

    return {
      id: isEdit ? automation.id : automationId,
      campagin_automation_id: null,
      isEdit: !!isEdit,
      name: derivedName,
      instaUserId: instaUserId || credentials?.user_id || "",
      instaId: instaId || credentials?.id || "",
      status: isEdit ? automation.status : "live",
      mediaData,
      data,
      followUps,
    };
  }, [
    media,
    automation,
    isEdit,
    buildFlowObject,
    commentRepliesEnabled,
    useAiReplies,
    aiContext,
    commentReplies,
    triggerType,
    keywords,
    automationName,
    instaUserId,
    instaId,
    credentials,
    staticMediaId,
    followUps,
  ]);

  // ─── Save handler ────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validateForm()) {
      enqueueSnackbar("Please fix the errors before saving", {
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = buildPayload();
      await callNonStoreApi(RegisterCommentToDm, payload);

      try {
        if (!isEdit) {
          await logAnalytics("automation_creation", { flow_id: payload.id });
          logMetaPixelEvent("AutomationCreated", true);
        } else {
          logMetaPixelEvent("AutomationUpdate", true);
        }
      } catch (_) {}

      enqueueSnackbar(
        isEdit ? "Automation updated" : "Automation is now live!",
        { variant: "success" }
      );

      initialSnapshotRef.current = getSnapshot();
      if (onSave) onSave();
      onClose();
    } catch (error) {
      if (error?.response?.status === 402) {
        setShowInsufficientCreditsDialog(true);
      } else {
        const msg = error?.response?.data?.error || "Something went wrong";
        enqueueSnackbar(msg, { variant: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePauseResume = async () => {
    if (!automation?.id) return;
    setIsLoading(true);
    try {
      const newStatus = automation.status === "live" ? "paused" : "live";
      const response = await callNonStoreApi(PauseOrResumeAutomationApi, {
        id: automation.id,
        status: newStatus,
      });
      if (response.data.message === "success") {
        enqueueSnackbar(
          `Automation ${
            response.data.status === "live" ? "is back live!" : "paused"
          }`,
          { variant: "success" }
        );
        if (onSave) onSave();
        onClose();
      } else {
        enqueueSnackbar("Something went wrong", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNextPost = async () => {
    if (!automation?.id) return;
    setIsLoading(true);
    try {
      const endpoint = DeleteAutomation.replace(":automationId", encodeURIComponent(automation.id));
      const res = await callNonStoreApiNew("delete", endpoint);
      if (res.status === 200 && res.data?.success) {
        enqueueSnackbar("Next Post automation deleted", { variant: "success" });
        if (onSave) onSave();
        onClose();
      } else {
        enqueueSnackbar("Couldn't delete automation", { variant: "error" });
      }
    } catch {
      enqueueSnackbar("Couldn't delete automation", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Keyword handlers ───────────────────────────────────────────

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim().toLowerCase();
    if (!trimmed) return;
    if (keywords.includes(trimmed)) {
      enqueueSnackbar("Keyword already added", { variant: "warning" });
      return;
    }
    setKeywords([...keywords, trimmed]);
    setKeywordInput("");
    if (dmMessageType === DM_MESSAGE_TYPES.ADVANCED) {
      setAdvancedResponses((prev) => ({
        ...prev,
        [trimmed]: { message: "", link: "", buttonText: "" },
      }));
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter((k) => k !== keyword));
    setAdvancedResponses((prev) => {
      const updated = { ...prev };
      delete updated[keyword];
      return updated;
    });
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleCommentReplyChange = (index, value) => {
    setCommentReplies((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAdvancedResponseChange = (keyword, field, value) => {
    setAdvancedResponses((prev) => ({
      ...prev,
      [keyword.toLowerCase()]: {
        ...prev[keyword.toLowerCase()],
        [field]: value,
      },
    }));
  };

  const handleAdvancedResponseImageUpload = async (keyword, file) => {
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const path = `automation-images/${generateUUID()}_${file.name}`;
      const url = await handleImageUpload(file, null, false, null, path);
      handleAdvancedResponseChange(keyword, "imageUrl", url);
    } catch (err) {
      enqueueSnackbar("Image upload failed", { variant: "error" });
    }
  };

  // ─── Computed values ─────────────────────────────────────────────

  const mediaThumbnail = useMemo(() => {
    return (
      media?.thumbnailUrl ||
      media?.mediaUrl ||
      automation?.mediaData?.mediaUrl ||
      ""
    );
  }, [media, automation]);

  const mediaTypeLabel = useMemo(() => {
    if (media?.isNextPost || automation?.mediaData?.isNextPost) return "Next Post";
    const mt =
      media?.mediaType ||
      media?.media_type ||
      automation?.mediaData?.media_type ||
      "";
    if (mt === "VIDEO" || mt === "REELS") return "Reel";
    if (mt === "CAROUSEL_ALBUM") return "Carousel";
    if (media?.isStory || automation?.mediaData?.isStory) return "Story";
    return "Post";
  }, [media, automation]);

  const captionText = useMemo(() => {
    return media?.caption || automation?.mediaData?.caption || "";
  }, [media, automation]);

  /**
   * Follow-ups only make sense when the main DM has at least one URL button
   * (label + link). Without a CTA, there's nothing for the user to click,
   * so the "responded" track is unreachable and the "notResponded" track
   * reduces to a blind nudge. Gate the feature on this.
   *
   * Note: `hasCTA` is declared earlier in the component (before
   * validateForm) so it can be referenced in validation deps without a TDZ.
   */

  const previewData = useMemo(() => {
    const otherAutomations = [];
    if (requireFollow) {
      otherAutomations.push({
        type: "request-to-follow",
        body: followPromptMessage,
      });
    }
    if (collectEmail) {
      otherAutomations.push({
        type: "request-email",
        body: emailPromptMessage,
      });
    }
    if (collectPhone) {
      otherAutomations.push({
        type: "request-mobile-number",
        body: phonePromptMessage,
      });
    }
    return {
      openingMessage: {
        message: "Hey! Thanks for your comment. Click below to continue.",
        buttonText: "Send me the link",
      },
      dmMessage: {
        message: dmMessage || "",
        link: dmButtons[0]?.link || "",
        buttonText: dmButtons[0]?.text || "",
        buttons: dmButtons.slice(0, MAX_BUTTONS),
        ...(dmImageUrl && { imageUrl: dmImageUrl }),
      },
      otherAutomations,
      followUps,
    };
  }, [
    dmMessage,
    dmButtons,
    dmImageUrl,
    requireFollow,
    followPromptMessage,
    collectEmail,
    emailPromptMessage,
    collectPhone,
    phonePromptMessage,
    followUps,
  ]);

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <FullPageOverlay>
      <PageHeader
        title={isEdit ? "Edit automation" : "Create automation"}
        subtitle={
          isEdit
            ? automation?.updatedAt
              ? `Last edited ${new Date(automation.updatedAt).toLocaleString()}`
              : "Editing automation"
            : "Set up a new comment-to-DM flow"
        }
        actions={
          <>
            {isEdit && automation?.status && (
              <Badge
                variant={automation.status === "live" ? "success" : "warning"}
              >
                {automation.status === "live" ? "Live" : "Paused"}
              </Badge>
            )}
            {isEdit && (
              <Button
                variant="secondary"
                size="md"
                onClick={handlePauseResume}
                disabled={isLoading}
              >
                {automation?.status === "live" ? "Pause" : "Resume"}
              </Button>
            )}
            {isEdit && (media?.isNextPost || automation?.mediaData?.isNextPost) && (
              <Button
                variant="secondary"
                size="md"
                onClick={handleDeleteNextPost}
                disabled={isLoading}
                style={{ color: "#ef4444", borderColor: "#fecaca" }}
              >
                Delete
              </Button>
            )}
            <Button variant="secondary" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={isLoading}
              onClick={handleSave}
            >
              Save automation
            </Button>
          </>
        }
      />

      <PageContainer maxWidth="xl" sx={{ pb: isDirty ? "96px" : "24px" }}>
        <Grid container spacing={4}>
        <Grid item xs={12} md={8} lg={8}>
        {/* ── Section 1: Trigger (post + keywords) ─────────────── */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <SectionLabel
              title="Trigger"
              description="Which post and keywords fire this automation."
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Card padding="lg">
              {/* Post preview */}
              <FieldLabel>Post</FieldLabel>
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                  mb: "20px",
                }}
              >
                {(media?.isNextPost || automation?.mediaData?.isNextPost) ? (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "8px",
                      flexShrink: 0,
                      backgroundColor: "#f0fdf4",
                      border: "1.5px dashed #86efac",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 28,
                    }}
                  >
                    ✨
                  </Box>
                ) : mediaThumbnail ? (
                  <Box
                    component="img"
                    src={mediaThumbnail}
                    alt=""
                    onError={(e) => {
                      e.target.style.visibility = "hidden";
                    }}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "8px",
                      objectFit: "cover",
                      flexShrink: 0,
                      backgroundColor: "#f1f5f9",
                    }}
                  />
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ mb: "4px" }}>
                    <Badge variant="neutral">{mediaTypeLabel}</Badge>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#334155",
                      lineHeight: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {(media?.isNextPost || automation?.mediaData?.isNextPost)
                      ? "This automation will fire on comments to your next Instagram post"
                      : captionText || "No caption"}
                  </Typography>
                </Box>
              </Box>

              {/* Automation name — optional, auto-derived from post on save */}
              <FieldLabel>Automation name (optional)</FieldLabel>
              <AntInput
                value={automationName}
                onChange={(e) => setAutomationName(e.target.value)}
                placeholder="e.g. Launch campaign DM"
                size="large"
                style={{ borderRadius: 8, marginBottom: 20 }}
              />

              {/* Trigger type */}
              <FieldLabel>Trigger</FieldLabel>
              <Box sx={{ display: "flex", gap: "10px", mb: "16px" }}>
                <TriggerOption
                  active={triggerType === "any"}
                  onClick={() => {
                    setTriggerType("any");
                    if (dmMessageType === DM_MESSAGE_TYPES.ADVANCED) {
                      setDmMessageType(DM_MESSAGE_TYPES.SIMPLE);
                    }
                  }}
                >
                  Any comment
                </TriggerOption>
                <TriggerOption
                  active={triggerType === "specific"}
                  onClick={() => setTriggerType("specific")}
                >
                  Specific keywords
                </TriggerOption>
              </Box>

              {triggerType === "specific" && (
                <Box>
                  <Box sx={{ display: "flex", gap: "8px", mb: "10px" }}>
                    <AntInput
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeywordKeyDown}
                      placeholder="Type a keyword and press Enter"
                      size="large"
                      style={{ flex: 1, borderRadius: 8 }}
                    />
                    <Button
                      variant="primary"
                      size="md"
                      leftIcon={<Plus size={16} />}
                      onClick={handleAddKeyword}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}
                  >
                    {keywords.map((kw) => (
                      <KeywordChip key={kw}>
                        {kw}
                        <Box
                          component="span"
                          onClick={() => handleRemoveKeyword(kw)}
                          sx={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            "&:hover": {
                              background: "rgba(249,115,22,0.2)",
                            },
                          }}
                        >
                          <X size={12} />
                        </Box>
                      </KeywordChip>
                    ))}
                  </Box>
                  <ErrorText>{errors.keywords}</ErrorText>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: "#e2e8f0" }} />

        {/* ── Section 2: DM message ───────────────────────────── */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <SectionLabel
              title="DM message"
              description="The message sent to users who comment."
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Card padding="lg">
              {triggerType === "specific" && keywords.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    background: "#f1f5f9",
                    borderRadius: "8px",
                    p: "3px",
                    mb: "16px",
                  }}
                >
                  <ReplyModeTab
                    active={dmMessageType === DM_MESSAGE_TYPES.SIMPLE}
                    onClick={() => setDmMessageType(DM_MESSAGE_TYPES.SIMPLE)}
                  >
                    Same for all
                  </ReplyModeTab>
                  <ReplyModeTab
                    active={dmMessageType === DM_MESSAGE_TYPES.ADVANCED}
                    onClick={() =>
                      setDmMessageType(DM_MESSAGE_TYPES.ADVANCED)
                    }
                  >
                    Per keyword
                  </ReplyModeTab>
                </Box>
              )}

              {dmMessageType === DM_MESSAGE_TYPES.SIMPLE ? (
                <Box>
                  <FieldLabel>Image (optional)</FieldLabel>
                  {dmImageUrl ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        p: "8px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        background: "#f8fafc",
                        mb: "16px",
                      }}
                    >
                      <Box
                        component="img"
                        src={dmImageUrl}
                        alt="DM"
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                      <Typography sx={{ fontSize: 13, color: "#334155" }}>
                        Image attached
                      </Typography>
                      <Box sx={{ ml: "auto" }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDmImageUrl(null)}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      component="label"
                      sx={{
                        cursor: isUploadingImage ? "not-allowed" : "pointer",
                        display: "block",
                        mb: "16px",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        disabled={isUploadingImage}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file || !file.type.startsWith("image/"))
                            return;
                          setIsUploadingImage(true);
                          try {
                            const path = `automation-images/${generateUUID()}_${
                              file.name
                            }`;
                            const url = await handleImageUpload(
                              file,
                              null,
                              false,
                              null,
                              path
                            );
                            setDmImageUrl(url);
                          } catch (err) {
                            enqueueSnackbar("Image upload failed", {
                              variant: "error",
                            });
                          } finally {
                            setIsUploadingImage(false);
                          }
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          p: "14px",
                          background: "#f8fafc",
                          border: "2px dashed #cbd5e1",
                          borderRadius: "8px",
                        }}
                      >
                        {isUploadingImage ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            Click to add an image
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  <FieldLabel>Message</FieldLabel>
                  <TextArea
                    rows={3}
                    value={dmMessage}
                    onChange={(e) => setDmMessage(e.target.value)}
                    placeholder="Hey! Here's what you asked for..."
                    style={{ borderRadius: 8, marginBottom: 12 }}
                  />
                  <ErrorText>{errors.dmMessage}</ErrorText>

                  {dmButtons.map((btn, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        mt: "12px",
                        p: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#0f172a",
                          }}
                        >
                          Button {idx + 1}
                        </Box>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setDmButtons((prev) => prev.filter((_, i) => i !== idx));
                            setErrors((prev) => {
                              const next = { ...prev };
                              if (next.dmButtons) {
                                const rest = { ...next.dmButtons };
                                delete rest[idx];
                                next.dmButtons = rest;
                              }
                              return next;
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setDmButtons((prev) => prev.filter((_, i) => i !== idx));
                            }
                          }}
                          aria-label={`Remove button ${idx + 1}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#64748b",
                            cursor: "pointer",
                            px: "6px",
                            py: "2px",
                            borderRadius: "6px",
                            "&:hover": {
                              color: "#b91c1c",
                              backgroundColor: "#fef2f2",
                            },
                            "&:focus-visible": {
                              outline: "2px solid #f97316",
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <X size={12} />
                          Remove
                        </Box>
                      </Box>
                      <FieldLabel>Link</FieldLabel>
                      <AntInput
                        value={btn.link}
                        onChange={(e) =>
                          setDmButtons((prev) =>
                            prev.map((b, i) =>
                              i === idx ? { ...b, link: e.target.value } : b
                            )
                          )
                        }
                        placeholder="https://example.com"
                        size="large"
                        style={{ borderRadius: 8 }}
                      />
                      <ErrorText>{errors.dmButtons?.[idx]}</ErrorText>
                      <Box sx={{ mt: "10px" }}>
                        <FieldLabel>Button text</FieldLabel>
                        <AntInput
                          value={btn.text}
                          onChange={(e) =>
                            setDmButtons((prev) =>
                              prev.map((b, i) =>
                                i === idx ? { ...b, text: e.target.value } : b
                              )
                            )
                          }
                          placeholder="Open Link"
                          size="large"
                          style={{ borderRadius: 8 }}
                        />
                      </Box>
                    </Box>
                  ))}

                  {dmButtons.length < MAX_BUTTONS && (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setDmButtons((prev) => [
                          ...prev,
                          { link: "", text: "Open Link" },
                        ])
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDmButtons((prev) => [
                            ...prev,
                            { link: "", text: "Open Link" },
                          ]);
                        }
                      }}
                      sx={{
                        mt: "12px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        px: "12px",
                        py: "8px",
                        borderRadius: "8px",
                        border: "1px dashed #cbd5e1",
                        backgroundColor: "#f8fafc",
                        color: "#475569",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        transition:
                          "border-color 150ms ease-out, background-color 150ms ease-out, color 150ms ease-out",
                        "&:hover": {
                          borderColor: "#f97316",
                          backgroundColor: "#fff7ed",
                          color: "#c2410c",
                        },
                        "&:focus-visible": {
                          outline: "2px solid #f97316",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Plus size={14} />
                      {dmButtons.length === 0
                        ? "Add a button (optional)"
                        : `Add another button (${MAX_BUTTONS - dmButtons.length} left)`}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <ErrorText>{errors.advancedResponses}</ErrorText>
                  {keywords.map((keyword) => {
                    const response = advancedResponses[
                      keyword.toLowerCase()
                    ] || { message: "", link: "", buttonText: "" };
                    return (
                      <Box
                        key={keyword}
                        sx={{
                          p: "14px",
                          background: "#f8fafc",
                          borderRadius: "8px",
                          mb: "10px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: primaryActionColor,
                            fontWeight: 600,
                            mb: "10px",
                          }}
                        >
                          Keyword: "{keyword}"
                        </Typography>
                        <TextArea
                          rows={2}
                          value={response.message}
                          onChange={(e) =>
                            handleAdvancedResponseChange(
                              keyword,
                              "message",
                              e.target.value
                            )
                          }
                          placeholder="DM message for this keyword..."
                          style={{ borderRadius: 8, marginBottom: 8 }}
                        />
                        {/* Image upload per keyword */}
                        {response.imageUrl ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              p: "6px",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              background: "#ffffff",
                              mb: "8px",
                            }}
                          >
                            <Box
                              component="img"
                              src={response.imageUrl}
                              alt=""
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                            <Typography sx={{ fontSize: 12, color: "#334155" }}>
                              Image attached
                            </Typography>
                            <Box sx={{ ml: "auto" }}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleAdvancedResponseChange(
                                    keyword,
                                    "imageUrl",
                                    ""
                                  )
                                }
                              >
                                Remove
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            component="label"
                            sx={{
                              display: "block",
                              cursor: "pointer",
                              mb: "8px",
                              p: "10px",
                              background: "#ffffff",
                              border: "2px dashed #cbd5e1",
                              borderRadius: "8px",
                              textAlign: "center",
                              fontSize: 12,
                              color: "#64748b",
                            }}
                          >
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleAdvancedResponseImageUpload(
                                  keyword,
                                  e.target.files?.[0]
                                )
                              }
                            />
                            Add image (optional)
                          </Box>
                        )}
                        <AntInput
                          value={response.link}
                          onChange={(e) =>
                            handleAdvancedResponseChange(
                              keyword,
                              "link",
                              e.target.value
                            )
                          }
                          placeholder="https://link-for-this-keyword.com"
                          size="large"
                          style={{ borderRadius: 8, marginBottom: 8 }}
                        />
                        {!isStringNullOrUndefined(response.link) && (
                          <AntInput
                            value={response.buttonText}
                            onChange={(e) =>
                              handleAdvancedResponseChange(
                                keyword,
                                "buttonText",
                                e.target.value
                              )
                            }
                            placeholder="Button text"
                            size="large"
                            style={{ borderRadius: 8 }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>


        </Grid>

        {/* ── Sticky right-side DM preview (desktop only) ──────── */}
        <Grid
          item
          md={4}
          lg={4}
          sx={{ display: { xs: "none", md: "block" } }}
        >
          <Box
            sx={{
              position: "sticky",
              top: "24px",
            }}
          >
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#64748b",
                mb: "10px",
              }}
            >
              Preview
            </Box>
            <Card padding="lg">
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <InstagramDMPreview automationData={previewData} />
              </Box>
            </Card>
          </Box>
        </Grid>
        </Grid>
      </PageContainer>

      {/* ── Sticky save bar ─────────────────────────────────── */}
      {isDirty && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "white",
            borderTop: "1px solid #e2e8f0",
            py: "12px",
            px: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 1200,
            boxShadow: "0 -1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            You have unsaved changes
          </Typography>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button variant="secondary" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={isLoading}
              onClick={handleSave}
            >
              Save changes
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Insufficient credits dialog ─────────────────────── */}
      <Dialog
        open={showInsufficientCreditsDialog}
        onClose={() => setShowInsufficientCreditsDialog(false)}
        PaperProps={{
          sx: { borderRadius: "12px", p: "24px", maxWidth: 400 },
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: "#0f172a",
              mb: "8px",
            }}
          >
            Insufficient credits
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "#64748b",
              mb: "24px",
              lineHeight: 1.5,
            }}
          >
            You don't have enough credits to create this automation. Please
            purchase more credits to continue.
          </Typography>
          <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setShowInsufficientCreditsDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                setShowInsufficientCreditsDialog(false);
                onClose();
              }}
            >
              Buy credits
            </Button>
          </Box>
        </Box>
      </Dialog>
    </FullPageOverlay>
  );
}
